import { createContext, useContext } from 'react';

export type EventPropertyValue = string | boolean;

export type MixpanelContextValue = {
    track: (event: string, properties?: Record<string, unknown>) => void;
    trackPageView: (properties?: Record<string, unknown>) => void;
    identify: (userId: string) => void;
    setUserProperties: (properties: Record<string, unknown>) => void;
    // Attached to every event from now on; an undefined value removes the property.
    setEventProperties: (properties: Record<string, EventPropertyValue | undefined>) => void;
    reset: () => void;
    // The user the SDK still holds from an earlier `identify`, persisted across visits until `reset`.
    storedUserId: () => string | undefined;
};

// Called before tracking as `userUuid`: whatever is still stored belongs to someone else (a session that ended
// while the tab was closed, another user on the same browser), and would otherwise claim this user's events.
export function forgetOtherStoredUser(mixpanel: MixpanelContextValue, userUuid: string): void {
    const stored = mixpanel.storedUserId();
    if (stored !== undefined && stored !== userUuid) {
        mixpanel.reset();
    }
}

export const MixpanelContext = createContext<MixpanelContextValue | undefined>(undefined);

export function useMixpanel(): MixpanelContextValue {
    const context = useContext(MixpanelContext);
    if (!context) {
        throw new Error('useMixpanel must be used within a MixpanelProvider');
    }
    return context;
}
